import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/shared/card';
import { Button } from '@/components/shared/button';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar,
  ResponsiveContainer,
  Tooltip
} from 'recharts';
import { AlertCircle, CheckCircle, Award, Flag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface QuickWin {
  title: string;
  description: string;
  effort: string;
  impact: string;
}

interface Challenge {
  area: string;
  recommendation: string;
}

interface Recommendations {
  overallScore: number;
  timeToValue: string;
  quickWins: QuickWin[];
  challenges: Challenge[];
  strongAreas: string[];
  weakAreas: string[];
}

interface ReadinessResultsProps {
  scores: Record<number, number>;
  recommendations: Recommendations;
  onReviewAssessment?: () => void;
  onSave?: () => Promise<void>;
  answers?: Record<number, string>;
}

const ReadinessResults = ({ scores, recommendations, onReviewAssessment, answers }: ReadinessResultsProps) => {
  const navigate = useNavigate();
  const dimensionScores = [
    { dimension: 'Data Inventory', score: scores[1] },
    { dimension: 'IT Infrastructure', score: scores[2] },
    { dimension: 'Skills & People', score: scores[3] },
    { dimension: 'Policy', score: scores[4] },
    { dimension: 'Leadership', score: scores[5] },
    { dimension: 'Public Buy-in', score: scores[6] },
    { dimension: 'Process Maturity', score: scores[7] }
  ];

  const sortedScores = [...dimensionScores].sort((a, b) => b.score - a.score);
  const strongestAreas = sortedScores.slice(0, 2);
  const weakestAreas = sortedScores.slice(-2);

  const handleUpdateClick = async () => {
    try {
      const { data: assessments, error } = await supabase
        .from('readiness_assessments')
        .select('data')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      const savedAnswers = assessments?.[0]?.data || {};
      console.log('Retrieved saved answers:', savedAnswers);
      
      if (Object.keys(savedAnswers).length === 0) {
        toast.error('No previous assessment found');
        return;
      }

      navigate('/readiness/assessment/wizard', { 
        state: { 
          savedAnswers,
          fromResults: true
        }
      });
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error('Failed to load previous assessment');
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8 pb-16">
      <Card className="border-2 border-[#3B50A2]/20">
        <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-[#3B50A2]/10 to-transparent">
          <CardTitle className="text-2xl md:text-3xl text-[#3B50A2]">
            Your AI Readiness Score: {recommendations.overallScore}/100
          </CardTitle>
          <Button 
            variant="outline"
            onClick={handleUpdateClick}
            className="ml-4"
          >
            Update Assessment
          </Button>
        </CardHeader>
        <CardContent className="space-y-8 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-6 rounded-lg bg-gradient-to-br from-[#00C0D9]/10 to-[#3B50A2]/5">
              <h3 className="text-lg font-medium text-[#3B50A2] mb-2">Strongest Areas</h3>
              <ul className="space-y-2">
                {strongestAreas.map((area, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-[#00C0D9] mr-2" />
                    <span className="text-gray-700">{area.dimension}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-lg bg-gradient-to-br from-[#ea384c]/10 to-[#800080]/5">
              <h3 className="text-lg font-medium text-[#800080] mb-2">Priority Gaps</h3>
              <ul className="space-y-2">
                {weakestAreas.map((area, index) => (
                  <li key={index} className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-[#ea384c] mr-2" />
                    <span className="text-gray-700">{area.dimension}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-lg bg-gradient-to-br from-[#F1AB11]/10 to-[#FFA500]/5">
              <h3 className="text-lg font-medium text-[#F1AB11] mb-2">Time to Value</h3>
              <p className="text-sm text-gray-600">Based on your readiness level:</p>
              <p className="text-xl font-bold text-[#F1AB11] mt-2">
                {recommendations.timeToValue}
              </p>
            </div>
          </div>
          
          <div className="h-[500px] w-full mt-8 p-4 bg-white rounded-lg shadow-sm">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart outerRadius={200} data={dimensionScores}>
                <PolarGrid stroke="#3B50A2" strokeOpacity={0.2} />
                <PolarAngleAxis 
                  dataKey="dimension" 
                  tick={{ fill: '#3B50A2', fontSize: 12 }}
                />
                <PolarRadiusAxis angle={30} domain={[0, 5]} stroke="#3B50A2" />
                <Radar
                  name="Your Score"
                  dataKey="score"
                  stroke="#CA0079"
                  fill="#CA0079"
                  fillOpacity={0.3}
                />
                <Tooltip 
                  formatter={(value: number) => [value.toFixed(1), "Score"]}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-[#800080]/20">
        <CardHeader className="bg-gradient-to-r from-[#800080]/10 to-transparent">
          <CardTitle className="flex items-center gap-2 text-[#800080]">
            <Award className="w-5 h-5" />
            Recommended Quick Wins
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recommendations.quickWins.map((win, index) => (
              <div key={index} className="p-6 border rounded-lg bg-gradient-to-br from-white to-[#3B50A2]/5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#800080] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-[#800080] mb-2">{win.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{win.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-[#555555]/10 text-[#555555] rounded-full text-xs">
                        Effort: {win.effort}
                      </span>
                      <span className="px-2 py-1 bg-[#555555]/10 text-[#555555] rounded-full text-xs">
                        Impact: {win.impact}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-[#ea384c]/20">
        <CardHeader className="bg-gradient-to-r from-[#ea384c]/10 to-transparent">
          <CardTitle className="flex items-center gap-2 text-[#ea384c]">
            <Flag className="w-5 h-5" />
            Priority Challenges
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {recommendations.challenges.map((challenge, index) => (
              <div key={index} className="p-6 border rounded-lg bg-gradient-to-br from-white to-[#3B50A2]/5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#ea384c] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-[#ea384c] mb-2">{challenge.area}</div>
                    <p className="text-sm text-gray-600">{challenge.recommendation}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReadinessResults;
